import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import axios from "utils/axiosConfig";
import { urlService } from "infra/envs";
import useNotifications from "hooks/useNotifications";

const isRequestCanceled = (error) =>
    error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

const ExternalRecruitersManagementContent = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [recruiters, setRecruiters] = useState([]);
    const [teams, setTeams] = useState([]);
    const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false);
    const [isLoadingTeams, setIsLoadingTeams] = useState(false);
    const [isSavingRecruiter, setIsSavingRecruiter] = useState(false);
    const [isSavingTeam, setIsSavingTeam] = useState(false);

    const [isRecruiterDialogOpen, setIsRecruiterDialogOpen] = useState(false);
    const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
    const [isTeamMembersDialogOpen, setIsTeamMembersDialogOpen] =
        useState(false);
    const [editingRecruiter, setEditingRecruiter] = useState(null);
    const [editingTeam, setEditingTeam] = useState(null);
    const [selectedTeamId, setSelectedTeamId] = useState(null);

    const [recruiterForm, setRecruiterForm] = useState({
        name: "",
        surname: "",
        email: "",
        password: "",
        team_id: "",
    });
    const [teamForm, setTeamForm] = useState({
        name: "",
    });

    const { renderErrorNotification, renderSuccessNotification } =
        useNotifications();
    const externalRecruitersPath = urlService.EXTERNAL_RECRUITERS_PATH;
    const teamsPath = urlService.TEAMS_PATH;

    const getTeamNameById = useCallback(
        (teamId) =>
            teams.find((team) => String(team.id) === String(teamId))?.name,
        [teams],
    );

    const fetchExternalRecruiters = async ({ signal } = {}) => {
        if (signal?.aborted) {
            return;
        }

        setIsLoadingRecruiters(true);
        try {
            const response = await axios.get(externalRecruitersPath, {
                signal,
            });
            if (signal?.aborted) {
                return;
            }
            setRecruiters(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            if (isRequestCanceled(error) || signal?.aborted) {
                return;
            }
            renderErrorNotification(
                error?.response?.data?.message ||
                    "Failed to fetch external recruiters",
            );
        } finally {
            if (!signal?.aborted) {
                setIsLoadingRecruiters(false);
            }
        }
    };

    const fetchTeams = useCallback(
        async ({ signal } = {}) => {
            if (signal?.aborted) {
                return [];
            }

            setIsLoadingTeams(true);
            try {
                const response = await axios.get(teamsPath, { signal });
                const fetchedTeams = Array.isArray(response?.data)
                    ? response.data
                    : [];
                if (!signal?.aborted) {
                    setTeams(fetchedTeams);
                }
                return fetchedTeams;
            } catch (error) {
                if (isRequestCanceled(error) || signal?.aborted) {
                    return [];
                }
                renderErrorNotification(
                    error?.response?.data?.message || "Failed to fetch teams",
                );
                return [];
            } finally {
                if (!signal?.aborted) {
                    setIsLoadingTeams(false);
                }
            }
        },
        [renderErrorNotification, teamsPath],
    );

    useEffect(() => {
        const controller = new AbortController();

        const initializeData = async () => {
            await Promise.all([
                fetchExternalRecruiters({ signal: controller.signal }),
                fetchTeams({ signal: controller.signal }),
            ]);
        };

        initializeData();

        return () => {
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateRecruiterDialog = async () => {
        await fetchTeams();

        setEditingRecruiter(null);
        setRecruiterForm({
            name: "",
            surname: "",
            email: "",
            password: "",
            team_id: "",
        });
        setIsRecruiterDialogOpen(true);
    };

    const openEditRecruiterDialog = useCallback(
        async (recruiter) => {
            await fetchTeams();

            setEditingRecruiter(recruiter);
            setRecruiterForm({
                name: recruiter?.name || "",
                surname: recruiter?.surname || "",
                email: recruiter?.email || "",
                password: "",
                team_id: recruiter?.team_id || "",
            });
            setIsRecruiterDialogOpen(true);
        },
        [fetchTeams],
    );

    const openCreateTeamDialog = () => {
        setEditingTeam(null);
        setTeamForm({ name: "" });
        setIsTeamDialogOpen(true);
    };

    const openEditTeamDialog = (team) => {
        setEditingTeam(team);
        setTeamForm({ name: team?.name || "" });
        setIsTeamDialogOpen(true);
    };

    const openTeamMembersDialog = async (team) => {
        setSelectedTeamId(team?.id ?? null);
        setIsTeamMembersDialogOpen(true);
        await fetchTeams();
    };

    const closeTeamMembersDialog = () => {
        setIsTeamMembersDialogOpen(false);
        setSelectedTeamId(null);
    };

    const upsertRecruiter = async () => {
        if (isSavingRecruiter) {
            return;
        }

        setIsSavingRecruiter(true);

        try {
            const latestTeams = await fetchTeams();
            const selectedTeamId = recruiterForm.team_id
                ? Number(recruiterForm.team_id)
                : null;

            if (!editingRecruiter?.id && !selectedTeamId) {
                renderErrorNotification("Team is required");
                return;
            }

            if (
                selectedTeamId &&
                !latestTeams.some((team) => Number(team.id) === selectedTeamId)
            ) {
                renderErrorNotification("Selected team is no longer available");
                return;
            }

            if (editingRecruiter?.id) {
                const payload = {
                    name: recruiterForm.name,
                    surname: recruiterForm.surname,
                    team_id: selectedTeamId,
                };

                await axios.put(
                    `${externalRecruitersPath}/${editingRecruiter.id}`,
                    payload,
                );
                renderSuccessNotification("External recruiter updated");
            } else {
                const payload = {
                    email: recruiterForm.email,
                    password: recruiterForm.password,
                    name: recruiterForm.name,
                    surname: recruiterForm.surname,
                    team_id: selectedTeamId,
                };

                await axios.post(externalRecruitersPath, payload);
                renderSuccessNotification("External recruiter created");
            }

            await Promise.all([fetchExternalRecruiters(), fetchTeams()]);

            setIsRecruiterDialogOpen(false);
            setEditingRecruiter(null);
            setRecruiterForm({
                name: "",
                surname: "",
                email: "",
                password: "",
                team_id: "",
            });
        } catch (error) {
            renderErrorNotification(
                error?.response?.data?.message ||
                    (editingRecruiter?.id
                        ? "Failed to update external recruiter"
                        : "Failed to create external recruiter"),
            );
        } finally {
            setIsSavingRecruiter(false);
        }
    };

    const upsertTeam = async () => {
        if (isSavingTeam) {
            return;
        }

        setIsSavingTeam(true);

        try {
            if (editingTeam?.id) {
                await axios.put(`${teamsPath}/${editingTeam.id}`, {
                    name: teamForm.name,
                });
                renderSuccessNotification("Team updated");
                await fetchTeams();
            } else {
                await axios.post(teamsPath, { name: teamForm.name });
                renderSuccessNotification("Team created");
                await fetchTeams();
            }

            setIsTeamDialogOpen(false);
            setEditingTeam(null);
            setTeamForm({ name: "" });
        } catch (error) {
            renderErrorNotification(
                error?.response?.data?.message ||
                    (editingTeam?.id
                        ? "Failed to update team"
                        : "Failed to create team"),
            );
        } finally {
            setIsSavingTeam(false);
        }
    };

    const recruiterColumns = useMemo(
        () => [
            { field: "id", sortable: false, headerName: "ID", width: 100 },
            {
                field: "name",
                headerName: "User Name",
                flex: 1,
                minWidth: 140,
                sortable: false,
                valueGetter: (params) =>
                    `${params?.row?.name || ""} ${params?.row?.surname || ""}`.trim(),
            },
            {
                field: "email",
                sortable: false,
                headerName: "Email",
                flex: 1,
                minWidth: 200,
            },
            {
                field: "team",
                headerName: "Team Name",
                flex: 1,
                minWidth: 140,
                sortable: false,
                valueGetter: (params) =>
                    params?.row?.team_name ||
                    getTeamNameById(params?.row?.team_id) ||
                    "-",
            },
            {
                field: "actions",
                headerName: "Actions",
                width: 130,
                sortable: false,
                renderCell: ({ row }) => (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={() => openEditRecruiterDialog(row)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" disabled>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                ),
            },
        ],
        [getTeamNameById, openEditRecruiterDialog],
    );

    const teamColumns = [
        { field: "id", headerName: "ID", width: 100, sortable: false },
        {
            field: "name",
            headerName: "Team Name",
            flex: 1,
            minWidth: 180,
            sortable: false,
        },
        {
            field: "team_members_count",
            headerName: "Team Members",
            width: 150,
            sortable: false,
            valueGetter: (params) => params?.row?.team_members_count ?? 0,
        },
        {
            field: "candidates_count",
            headerName: "Candidates",
            width: 140,
            sortable: false,
            valueGetter: (params) => params?.row?.candidates_count ?? 0,
        },
        {
            field: "members",
            headerName: "Members",
            width: 120,
            sortable: false,
            renderCell: ({ row }) => (
                <Button size="small" onClick={() => openTeamMembersDialog(row)}>
                    View
                </Button>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 130,
            sortable: false,
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        size="small"
                        onClick={() => openEditTeamDialog(row)}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" disabled>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ),
        },
    ];

    const selectedTeam = useMemo(
        () =>
            teams.find((team) => String(team.id) === String(selectedTeamId)) ||
            null,
        [selectedTeamId, teams],
    );

    const selectedTeamMembers = useMemo(
        () =>
            Array.isArray(selectedTeam?.members) ? selectedTeam.members : [],
        [selectedTeam],
    );

    return (
        <>
            <Paper sx={{ borderRadius: "10px", p: 2 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                >
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => setActiveTab(newValue)}
                    >
                        <Tab label="External Recruiters" />
                        <Tab label="Teams" />
                    </Tabs>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={
                            activeTab === 0
                                ? openCreateRecruiterDialog
                                : openCreateTeamDialog
                        }
                    >
                        {activeTab === 0 ? "Add Recruiter" : "Add Team"}
                    </Button>
                </Stack>

                {activeTab === 0 ? (
                    <Box sx={{ width: "100%" }}>
                        <DataGrid
                            rows={recruiters}
                            columns={recruiterColumns}
                            loading={isLoadingRecruiters}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooter
                            disableColumnMenu
                            disableColumnSorting
                        />
                    </Box>
                ) : (
                    <Box sx={{ width: "100%" }}>
                        <DataGrid
                            rows={teams}
                            columns={teamColumns}
                            loading={isLoadingTeams}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooter
                            disableColumnMenu
                            disableColumnSorting
                        />
                    </Box>
                )}
            </Paper>

            <Dialog
                open={isRecruiterDialogOpen}
                onClose={() => {
                    if (!isSavingRecruiter) {
                        setIsRecruiterDialogOpen(false);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {editingRecruiter ? "Edit Recruiter" : "Create Recruiter"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="First Name"
                            value={recruiterForm.name}
                            onChange={(event) =>
                                setRecruiterForm((prev) => ({
                                    ...prev,
                                    name: event.target.value,
                                }))
                            }
                            disabled={isSavingRecruiter}
                            fullWidth
                        />
                        <TextField
                            label="Last Name"
                            value={recruiterForm.surname}
                            onChange={(event) =>
                                setRecruiterForm((prev) => ({
                                    ...prev,
                                    surname: event.target.value,
                                }))
                            }
                            disabled={isSavingRecruiter}
                            fullWidth
                        />
                        {editingRecruiter ? (
                            <FormControl fullWidth disabled={isSavingRecruiter}>
                                <InputLabel id="team-select-label">
                                    Team
                                </InputLabel>
                                <Select
                                    labelId="team-select-label"
                                    label="Team"
                                    value={recruiterForm.team_id}
                                    onChange={(event) =>
                                        setRecruiterForm((prev) => ({
                                            ...prev,
                                            team_id: event.target.value,
                                        }))
                                    }
                                >
                                    <MenuItem value="">
                                        <em>No team</em>
                                    </MenuItem>
                                    {teams.map((team) => (
                                        <MenuItem key={team.id} value={team.id}>
                                            {team.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <TextField
                                label="Email"
                                type="email"
                                value={recruiterForm.email}
                                onChange={(event) =>
                                    setRecruiterForm((prev) => ({
                                        ...prev,
                                        email: event.target.value,
                                    }))
                                }
                                disabled={isSavingRecruiter}
                                fullWidth
                            />
                        )}
                        {!editingRecruiter && (
                            <TextField
                                label="Password"
                                type="password"
                                value={recruiterForm.password}
                                onChange={(event) =>
                                    setRecruiterForm((prev) => ({
                                        ...prev,
                                        password: event.target.value,
                                    }))
                                }
                                disabled={isSavingRecruiter}
                                fullWidth
                            />
                        )}
                        {!editingRecruiter && (
                            <FormControl
                                fullWidth
                                required
                                disabled={isSavingRecruiter}
                            >
                                <InputLabel id="team-select-create-label">
                                    Team
                                </InputLabel>
                                <Select
                                    labelId="team-select-create-label"
                                    label="Team"
                                    value={recruiterForm.team_id}
                                    onChange={(event) =>
                                        setRecruiterForm((prev) => ({
                                            ...prev,
                                            team_id: event.target.value,
                                        }))
                                    }
                                >
                                    {teams.map((team) => (
                                        <MenuItem key={team.id} value={team.id}>
                                            {team.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsRecruiterDialogOpen(false)}
                        disabled={isSavingRecruiter}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={upsertRecruiter}
                        loading={isSavingRecruiter}
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isTeamDialogOpen}
                onClose={() => {
                    if (!isSavingTeam) {
                        setIsTeamDialogOpen(false);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {editingTeam ? "Edit Team" : "Create Team"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Team Name"
                            value={teamForm.name}
                            onChange={(event) =>
                                setTeamForm((prev) => ({
                                    ...prev,
                                    name: event.target.value,
                                }))
                            }
                            disabled={isSavingTeam}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsTeamDialogOpen(false)}
                        disabled={isSavingTeam}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={upsertTeam}
                        loading={isSavingTeam}
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isTeamMembersDialogOpen}
                onClose={closeTeamMembersDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {selectedTeam?.name
                        ? `${selectedTeam.name} Members`
                        : "Team Members"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1} mt={1}>
                        {isLoadingTeams ? (
                            <Typography variant="body2" color="text.secondary">
                                Loading latest team members...
                            </Typography>
                        ) : selectedTeamMembers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No members assigned to this team.
                            </Typography>
                        ) : (
                            selectedTeamMembers.map((member) => (
                                <Typography key={member.id} variant="body2">
                                    {`${member?.name || ""} ${member?.surname || ""}`.trim()}{" "}
                                    ({member?.email || "No email"})
                                </Typography>
                            ))
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeTeamMembersDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ExternalRecruitersManagementContent;
